import React, { useMemo } from 'react';
import { useGetList, useGetMany, useGetOne, useTranslate, useResourceContext } from 'react-admin';
import { Box, Typography, Paper, Chip, Divider, CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export interface AuditTimelineProps {
  resourceType?: string; // Optional, will use useResourceContext if not provided
  resourceId: number | string;
  compact?: boolean;
  limit?: number;
  onLogClick?: (log: AuditLog) => void;
}

export interface AuditLog {
  id: number;
  userId: number;
  resourceType: string;
  resourceId: number;
  action: 'create' | 'update' | 'delete' | 'access' | 'attach' | 'detach';
  changes?: {
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
    fields?: string[];
  } | null;
  metadata?: Record<string, unknown> | null;
  parentResourceType?: string | null;
  parentResourceId?: number | null;
  createdAt: string;
  updatedAt: string;
}

const actionColors: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
  access: 'default',
  attach: 'info',
  detach: 'warning',
};

const actionLabels: Record<string, string> = {
  create: 'Criado',
  update: 'Atualização',
  delete: 'Excluído',
  access: 'Acessado',
  attach: 'Anexado',
  detach: 'Desanexado',
};

export const AuditTimeline = ({
  resourceType: propResourceType,
  resourceId,
  compact = false,
  limit = 50,
  onLogClick,
}: AuditTimelineProps) => {
  const contextResourceType = useResourceContext();
  const resourceType = propResourceType || contextResourceType || 'resource';
  // Don't render anything if resourceId is invalid (e.g., during create)
  // Check if resourceId is 'create' (string) or if it's not a valid positive integer
  const resourceIdString = resourceId ? String(resourceId) : '';
  const resourceIdNumber = resourceId ? Number(resourceId) : NaN;
  
  const isValidResourceId = 
    resourceId && 
    resourceIdString !== 'create' && 
    resourceIdString !== '' &&
    !isNaN(resourceIdNumber) && 
    resourceIdNumber > 0 && 
    Number.isInteger(resourceIdNumber);

  // Early return if resourceId is invalid - don't render anything
  if (!isValidResourceId) {
    return null;
  }

  const { data, isLoading, error } = useGetList<AuditLog>(
    'audit',
    {
      pagination: { page: 1, perPage: limit },
      sort: { field: 'createdAt', order: 'DESC' },
      filter: { resourceType, resourceId: resourceIdNumber },
    },
    { enabled: isValidResourceId }
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="error">
          Erro ao carregar histórico de auditoria
        </Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          Nenhum registro de auditoria encontrado
        </Typography>
      </Box>
    );
  }

  if (compact) {
    return <CompactTimeline logs={data} onLogClick={onLogClick} resourceType={resourceType} />;
  }

  return <FullTimeline logs={data} onLogClick={onLogClick} resourceType={resourceType} />;
};

const FullTimeline = ({ logs, onLogClick, resourceType }: { logs: AuditLog[]; onLogClick?: (log: AuditLog) => void; resourceType: string }) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Histórico de Auditoria
      </Typography>
      <Box mt={2}>
        {logs.map((log, index) => (
          <Box key={log.id}>
            <TimelineItem log={log} onLogClick={onLogClick} resourceType={resourceType} />
            {index < logs.length - 1 && <Divider sx={{ my: 1 }} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Helper function to generate translated description for related changes
const getRelatedChangeDescription = (
  metadata: Record<string, unknown>, 
  translate: (key: string, options?: any) => string
): string => {
  const relatedResourceType = metadata.relatedResourceType as string;
  
  // Status change
  if (relatedResourceType === 'job-application-status' && metadata.statusNameChanged) {
    const oldName = metadata.oldStatusName as string;
    const newName = metadata.newStatusName as string;
    return translate('resources.job-application.audit.relatedChanges.statusRenamed', {
      oldName,
      newName,
      _: `Status "${oldName}" foi renomeado para "${newName}"`,
    });
  }
  
  // Source change
  if (relatedResourceType === 'job-application-source' && metadata.sourceNameChanged) {
    const oldName = metadata.oldSourceName as string;
    const newName = metadata.newSourceName as string;
    return translate('resources.job-application.audit.relatedChanges.sourceRenamed', {
      oldName,
      newName,
      _: `Fonte "${oldName}" foi renomeada para "${newName}"`,
    });
  }
  
  // Company change
  if (relatedResourceType === 'company' && metadata.companyNameChanged) {
    const oldName = metadata.oldCompanyName as string;
    const newName = metadata.newCompanyName as string;
    return translate('resources.job-application.audit.relatedChanges.companyRenamed', {
      oldName,
      newName,
      _: `Empresa "${oldName}" foi renomeada para "${newName}"`,
    });
  }
  
  // User change
  if (relatedResourceType === 'user' && metadata.userNameChanged) {
    const oldName = metadata.oldUserName as string;
    const newName = metadata.newUserName as string;
    return translate('resources.job-application.audit.relatedChanges.userRenamed', {
      oldName,
      newName,
      _: `Usuário "${oldName}" foi renomeado para "${newName}"`,
    });
  }
  
  return translate('resources.job-application.audit.relatedChanges.generic', {
    _: 'Recurso relacionado foi alterado',
  });
};

const CompactTimeline = ({ logs, onLogClick, resourceType }: { logs: AuditLog[]; onLogClick?: (log: AuditLog) => void; resourceType: string }) => {
  const translate = useTranslate();
  
  const getFieldLabel = (field: string): string => {
    // Use resource-specific i18n for field labels with fallback to field name
    return translate(`resources.${resourceType}.fields.${field}`);
  };

  return (
    <Box>
      {logs.slice(0, 5).map((log) => {
        const isRelatedChange = log.metadata?.isRelatedChange;
        const changedFields = (log.changes?.fields || []).filter(field => field !== 'relatedResource');
        const hasChanges = changedFields.length > 0;

        return (
          <Box
            key={log.id}
            mb={1}
            onClick={() => onLogClick?.(log)}
            sx={{
              cursor: onLogClick ? 'pointer' : 'default',
              '&:hover': onLogClick
                ? {
                    opacity: 0.8,
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s',
                  }
                : {},
            }}
          >
            <TimelineItem log={log} compact>
              {isRelatedChange && (
                <Box mt={0.5}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                    🔗 {getRelatedChangeDescription(log.metadata || {}, translate)}
                  </Typography>
                </Box>
              )}
              {!isRelatedChange && hasChanges && (
                <Box mt={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    {changedFields.slice(0, 3).map(getFieldLabel).join(', ')}
                    {changedFields.length > 3 && ` +${changedFields.length - 3} mais`}
                  </Typography>
                </Box>
              )}
            </TimelineItem>
          </Box>
        );
      })}
      {logs.length > 5 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          +{logs.length - 5} mais registros
        </Typography>
      )}
    </Box>
  );
};

const TimelineItem = ({
  log,
  compact = false,
  onLogClick,
  children,
  resourceType,
}: {
  log: AuditLog;
  compact?: boolean;
  onLogClick?: (log: AuditLog) => void;
  children?: React.ReactNode;
  resourceType: string;
}) => {
  const translate = useTranslate();
  const actionLabel = actionLabels[log.action] || log.action;
  const actionColor = actionColors[log.action] || 'default';
  const date = format(new Date(log.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });

  // Fetch user for this log
  const { data: user } = useGetOne('user', { id: log.userId }, { enabled: !!log.userId });

  const userDisplay = useMemo(() => {
    if (!user) return `ID: ${log.userId}`;
    if (user.name && user.email) return `${user.name} <${user.email}>`;
    return user.name || user.email || `ID: ${log.userId}`;
  }, [user, log.userId]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1 : 2,
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s',
        ...(onLogClick && {
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'primary.main',
            transform: 'translateX(2px)',
          },
        }),
      }}
      onClick={() => onLogClick?.(log)}
    >
      <Box display="flex" alignItems="center" gap={1} mb={compact ? 0.5 : 1} flexWrap="wrap">
        <Chip
          label={actionLabel}
          color={actionColor}
          size={compact ? 'small' : 'medium'}
          sx={{ fontWeight: 500 }}
        />
        <Typography variant="caption" color="text.secondary">
          {date}
        </Typography>
        {!compact && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>
              •
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translate(`resources.${resourceType}.audit.user`, {
                _: translate('common.audit.user')
              })}: {userDisplay}
            </Typography>
          </>
        )}
      </Box>

      {/* Show related resource changes */}
      {log.metadata?.isRelatedChange && !compact && (
        <Box mt={1}>
          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
            🔗 {getRelatedChangeDescription(log.metadata, translate)}
          </Typography>
        </Box>
      )}

      {/* Show regular field changes - filter out relatedResource */}
      {log.changes?.fields && 
       log.changes.fields.length > 0 && 
       !log.metadata?.isRelatedChange &&
       !compact && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Campos alterados:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
            {log.changes.fields
              .filter(field => field !== 'relatedResource')
              .map((field) => (
                <Chip
                  key={field}
                  label={field}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
          </Box>
        </Box>
      )}

      {/* Show additional metadata (only if not related change and not compact) */}
      {log.metadata && 
       !log.metadata.isRelatedChange && 
       Object.keys(log.metadata).length > 0 && 
       !compact && (
        <Box mt={1}>
          {log.metadata.statusChanged && (
            <Typography variant="caption" color="info.main" sx={{ fontWeight: 500 }}>
              Status alterado: {log.metadata.oldStatusId} → {log.metadata.newStatusId}
            </Typography>
          )}
        </Box>
      )}

      {/* Custom children (for compact view enhancements) */}
      {children}
    </Paper>
  );
};

